import React from 'react';
import { Search, Cog, Upload, Trash2, CheckCircle } from 'lucide-react';

const steps = [
  {
    number: '01',
    icon: Search,
    title: 'Audit & Extraction',
    description: 'Lecture sécurisée de vos bases On-Prem sans impact sur les performances de production.',
    color: 'primary'
  },
  {
    number: '02',
    icon: Cog,
    title: 'Transformation ETL',
    description: 'Conversion au format JSON Auth0 avec mapping complexe et nettoyage de données.',
    color: 'secondary'
  },
  {
    number: '03',
    icon: Upload,
    title: 'Import & Intégrité',
    description: 'Injection via l\'API Management avec tests de somme de contrôle (checksum) pour garantir l\'intégrité de 100% des comptes.',
    color: 'primary'
  },
  {
    number: '04',
    icon: Trash2,
    title: 'Purge Sécurisée',
    description: 'Une fois la validation post-migration confirmée, nous gérons le décommissionnement et la suppression définitive des données sur vos serveurs On-Prem pour réduire votre surface d\'attaque.',
    color: 'secondary'
  }
];

export default function SecurityProtocol() {
  return (
    <section id="protocol" className="py-20 px-4 relative">
      {/* Background gradient accent */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-transparent pointer-events-none" />
      
      <div className="container mx-auto max-w-6xl relative">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass border border-secondary/20 mb-6">
            <CheckCircle className="w-4 h-4 text-secondary" />
            <span className="text-sm text-muted-foreground">Protocole Certifié</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Le Protocole de
            <span className="text-gradient-blue"> Sécurité</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Un processus technique rigoureux en 4 étapes pour garantir une migration sans faille
          </p>
        </div>

        {/* Steps */}
        <div className="space-y-8">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isEven = index % 2 === 0;
            
            return (
              <div 
                key={index}
                className="relative"
              >
                {/* Connector line - hidden on last item */}
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute left-1/2 top-20 w-0.5 h-full bg-border -translate-x-1/2" />
                )}
                
                <div className={`grid md:grid-cols-2 gap-8 items-center ${
                  isEven ? '' : 'md:flex-row-reverse'
                }`}>
                  {/* Content */}
                  <div className={`${
                    isEven ? 'md:text-right md:pr-12' : 'md:order-2 md:pl-12'
                  }`}>
                    <div className={`inline-flex items-center gap-3 mb-4 ${
                      isEven ? 'md:flex-row-reverse' : ''
                    }`}>
                      <span className={`text-5xl font-bold ${
                        step.color === 'primary' ? 'text-primary/20' : 'text-secondary/20'
                      }`}>
                        {step.number}
                      </span>
                      <h3 className="text-2xl font-semibold">{step.title}</h3>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                  
                  {/* Icon */}
                  <div className={`flex justify-center md:justify-${
                    isEven ? 'start' : 'end'
                  } ${isEven ? '' : 'md:order-1'}`}>
                    <div className="relative">
                      <div className={`w-24 h-24 rounded-2xl glass flex items-center justify-center ${
                        step.color === 'primary' ? 'glow-purple' : 'glow-blue'
                      } hover-lift group`}>
                        <Icon className={`w-12 h-12 ${
                          step.color === 'primary' ? 'text-primary' : 'text-secondary'
                        } group-hover:scale-110 transition-transform`} strokeWidth={1.5} />
                      </div>
                      {/* Connection dot */}
                      <div className={`hidden md:block absolute top-1/2 ${
                        isEven ? '-right-14' : '-left-14'
                      } w-4 h-4 rounded-full ${
                        step.color === 'primary' ? 'bg-primary' : 'bg-secondary'
                      } -translate-y-1/2 ring-4 ring-background`} />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-16 pt-16 border-t border-border">
          <div className="text-center">
            <div className="text-3xl font-bold text-gradient-purple mb-1">4</div>
            <div className="text-sm text-muted-foreground">Étapes sécurisées</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-gradient-blue mb-1">100%</div>
            <div className="text-sm text-muted-foreground">Intégrité garantie</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-gradient-purple mb-1">0</div>
            <div className="text-sm text-muted-foreground">Downtime</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-gradient-blue mb-1">24/7</div>
            <div className="text-sm text-muted-foreground">Support dédié</div>
          </div>
        </div>
      </div>
    </section>
  );
}
